import { FormGroup, Label } from "reactstrap";
import __ from "src/utils/Translations";
import Select from "react-select";
import React, { useCallback, useMemo, useState } from "react";
import { AxiosError } from "axios";
import useFetchLocalizationsData from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchLocalizationsData";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import { debounce } from "lodash";

interface ILocalizationsField {
  companyId: string;
  structureId: string;
  value: any;
  onChange: (value: any) => void;
  isEditable: boolean;
}

const LocalizationsField = ({
  companyId,
  structureId,
  value,
  onChange,
  isEditable,
}: ILocalizationsField) => {
  const logStatus = useLogStatus();
  const [inputValue, setInputValue] = useState<string>("");

  const {
    data,
    isFetching,
    hasNextPage,
    fetchNextPage,
    error: localizationsError,
  } = useFetchLocalizationsData(companyId, structureId, inputValue);

  if (localizationsError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "LocalizationsField: fetchLocalizationsData failed",
      statusDetails: {
        source: "API",
        operation: "fetchLocalizationsData",
        status: (localizationsError as AxiosError)?.response?.status,
        url: (localizationsError as AxiosError)?.config?.url,
      },
    });
  }

  const fetchedLocalizations = useMemo(
    () =>
      data?.pages.flatMap((page) =>
        page.content.map((loc) => ({
          value: loc.id,
          label: [loc.streetAddress, loc.postalCode, loc.city]
            .filter(Boolean)
            .join(", "),
        }))
      ) || [],
    [data]
  );

  const handleInputChange = useCallback(
    debounce((value) => {
      setInputValue(value);
    }, 1000),
    []
  );

  const handleScrollToBottom = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <FormGroup>
      <Label for="localization">{__("Lokalizacja")}</Label>
      <Select
        id="localization"
        value={value}
        onChange={onChange}
        menuPlacement="auto"
        options={fetchedLocalizations}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={handleScrollToBottom}
        placeholder={__("Wybierz")}
        noOptionsMessage={() => {
          if (isFetching) return __("Ładowanie...");
          return fetchedLocalizations.length > 0
            ? null
            : __("Brak wyników wyszukiwania");
        }}
        loadingMessage={() => __("Ładowanie...")}
        isClearable
        isLoading={isFetching}
        isDisabled={!isEditable}
      />
    </FormGroup>
  );
};

export default LocalizationsField;
